import * as React from "react";
import Sidebar from "../Dashboard/Sidebar.js";
import { Box } from "@mui/system";
import { styled } from "@mui/material/styles";
import { Grid, Typography, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";

// Icons
import usersIcon from "../../Images/admin-users.png";
import modelsIcon from "../../Images/admin-models.png";
import carInfoIcon from "../../Images/admin-info.png";
import reportsIcon from "../../Images/admin-reports.png";
import closeReason from "../../Images/CloseReasons.png";
import carGarage from "../../Images/car-garage.png";
import blogsIcon from "../../Images/blogs.png";
import enquiryIcon from "../../Images/enquiry.png";

import { enquiryService } from "../../services/enquiryService";

import "./Styles/AdminPanel.css";

// 1. Refined Glassmorphism Styled Card
const GlassCard = styled(Paper)(({ theme }) => ({
  // Glass Effect
  background: "rgba(255, 255, 255, 0.4)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255, 255, 255, 0.5)",

  // Sizing & Layout
  padding: theme.spacing(3),
  textAlign: "center",
  borderRadius: "24px",
  cursor: "pointer",
  color: "#1a1a1a",
  transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minHeight: "190px", // Smaller footprint
  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)",

  "&:hover": {
    transform: "translateY(-10px)",
    background: "rgba(255, 255, 255, 0.8)",
    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
    borderColor: "#33333367",
    "& .icon-wrapper": {
      // backgroundColor: "#eb1d27",
      transform: "scale(1.1)",
      "& img": {
        // filter: "brightness(0) invert(1)",
      },
    },
    "& h3": {
      color: "#1a1a1a",
    },
  },
}));

function AdminPanel(props) {
  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #F8F9FB 0%, #E2E8F0 100%)",
        minHeight: "100vh",
        display: "flex",
      }}
    >
      <Sidebar />
      <AdminConsole />
    </Box>
  );
}

const AdminConsole = () => {
  const navigate = useNavigate();
  const [pendingEnquiriesCount, setPendingEnquiriesCount] = React.useState(0);

  React.useEffect(() => {
    const fetchPendingEnquiries = async () => {
      try {
        const result = await enquiryService.getEnquiries();
        if (!result || result.success === false) {
          setPendingEnquiriesCount(0);
          return;
        }

        const list = Array.isArray(result.data)
          ? result.data
          : Array.isArray(result.data?.data)
            ? result.data.data
            : [];

        const count = list.filter(
          (enquiry) => (enquiry.status || "").toLowerCase() === "new",
        ).length;
        setPendingEnquiriesCount(count);
      } catch (e) {
        console.error("Failed to fetch enquiries for admin panel card", e);
        setPendingEnquiriesCount(0);
      }
    };

    fetchPendingEnquiries();
  }, []);

  const customNavigate = (path) => {
    navigate(`/${path}`);
  };

  const AdminCard = ({ icon, title, desc, path, badge }) => (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      {" "}
      {/* LG=3 allows 4 cards per row */}
      <GlassCard elevation={0} onClick={() => customNavigate(path)}>
        <Box
          className="icon-wrapper"
          sx={{
            width: "60px",
            height: "60px",
            background: "rgba(255,255,255,0.6)",
            borderRadius: "18px",
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <img
            src={icon}
            style={{ height: "30px", width: "30px", transition: "0.3s" }}
            alt={title}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography
            variant="body1"
            component="h3"
            sx={{
              fontWeight: 800,
              transition: "0.3s",
              fontSize: "1.1rem",
            }}
          >
            {title}
          </Typography>
          {badge > 0 && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "999px",
                backgroundColor: "#FEE2E2",
                color: "#B91C1C",
                fontSize: "0.7rem",
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {badge}
            </Box>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{ color: "#555", lineHeight: 1.4, px: 1 }}
        >
          {desc}
        </Typography>
      </GlassCard>
    </Grid>
  );

  return (
    <Box
      flex={4}
      sx={{
        ml: { xs: 0, md: "18vw" },
        p: { xs: 3, md: 4, lg: 6 },
        width: "100%",
        pt: { xs: "84px", md: 4 }, // For fixed mobile sidebar top bar
      }}
    >
      {/* Modern Minimal Header */}
      <Box sx={{ mb: 6, textAlign: "left" }}>
        <Typography
          variant="overline"
          sx={{
            color: "#eb1d27",
            fontWeight: 900,
            letterSpacing: "2px",
            display: "block",
          }}
        >
          System Control
        </Typography>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            color: "#1a1a1a",
            fontFamily: "Kamerik, sans-serif",
            mt: 0.5,
            fontSize: { xs: "2rem", md: "3rem" },
          }}
        >
          Admin Console
        </Typography>
        <Box
          sx={{
            width: "40px",
            height: "4px",
            background: "#eb1d27",
            mt: 1.5,
            borderRadius: "10px",
          }}
        />
      </Box>

      {/* Compact Grid */}
      <Grid container spacing={3}>
        <AdminCard
          icon={usersIcon}
          title="Users"
          desc="Manage team access and profile permissions."
          path="Users"
        />
        <AdminCard
          icon={modelsIcon}
          title="Models"
          desc="Update the vehicle catalog specifications."
          path="VehicleModels"
        />
        <AdminCard
          icon={carInfoIcon}
          title="Car Facts"
          desc="Edit technical data and luxury highlights."
          path="carFacts"
        />
        <AdminCard
          icon={reportsIcon}
          title="Reports"
          desc="Monitor category-wide business performance."
          path="reports"
        />
        <AdminCard
          icon={enquiryIcon}
          title="Enquiries"
          desc="Manage incoming customer enquiries."
          path="admin/enquiries"
          badge={pendingEnquiriesCount}
        />
        <AdminCard
          icon={closeReason}
          title="Reasons"
          desc="Refine lead conversion and drop-off logic."
          path="closingReasons"
        />
        <AdminCard
          icon={carGarage}
          title="Inventory"
          desc="Manage website inventory and stock."
          path="Inventory"
        />
        <AdminCard
          icon={blogsIcon}
          title="Blogs"
          desc="Create and manage blog posts with SEO."
          path="blogs"
        />
        <AdminCard
          icon={reportsIcon}
          title="Push Notifications"
          desc="Manage push subscriptions and send test alerts."
          path="admin/push-notifications"
        />
      </Grid>
    </Box>
  );
};

export default AdminPanel;
